import React, { useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { cx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIconRender from '@atlaskit/icon/core/chevron-up';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
import Tooltip from '@atlaskit/tooltip';

import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { usePageContentDimensions } from '@confluence/object-comments/entry-points/usePageContentDimensions.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { VIEW_OMNIBAR_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	useShouldHideOmniBar,
	useHasReceivedOmniBarHideRequest,
} from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import type { OmniBarProps } from './omniBarTypes.ts';
import { OmniBarReactions } from './OmnibarReactions.tsx';
import { useOmnibarEligibilityFromContext } from './OmnibarEligibilityContext.tsx';
import { useOmnibarReactionsData } from './hooks/useOmnibarReactionsData.ts';
import { useOmnibarCommentTrigger } from './hooks/useOmnibarCommentTrigger.ts';

const logger = getLogger('experiment-omnibar');
const OMNIBAR_ANALYTICS_SOURCE = 'confluenceOmniBar';

/** Suppress the initial mount transition, but allow it on later remounts so the OmniBar can slide back in from the bottom of the page. */
let hasEverMounted = false;

function wasOmnibarSSRd(): boolean {
	if (typeof document === 'undefined') {
		return false;
	}
	const omnibarElement = document.querySelector('[data-testid="omni-bar"]');
	if (!omnibarElement) {
		return false;
	}
	const computedElementStyle = window.getComputedStyle(omnibarElement);
	return computedElementStyle.position === 'sticky' && computedElementStyle.visibility !== 'hidden';
}

function getPinStateFromStorage(): boolean {
	try {
		const stored = confluenceLocalStorageInstance?.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_OMNIBAR_PINNED,
		);
		if (stored === 'true') return true;
		if (stored === 'false') return false;
	} catch (e) {
		logger.error`Omnibar failed to read pin state from storage: ${e}`;
	}
	return true;
}

function persistPinState(pinned: boolean): void {
	try {
		confluenceLocalStorageInstance?.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_OMNIBAR_PINNED,
			pinned ? 'true' : 'false',
		);
	} catch (e) {
		logger.error`Omnibar failed to persist pin state to storage: ${e}`;
	}
}

const i18n = defineMessages({
	hideOmniBar: {
		id: 'experiment-omnibar.omni-bar.hide-button',
		defaultMessage: 'Hide toolbar',
		description:
			'The text is shown as the accessible label for a button in the OmniBar toolbar. When clicked, the button hides (collapses) the toolbar from view.',
	},
	pinOmniBar: {
		id: 'experiment-omnibar.omni-bar.pin-button',
		defaultMessage: 'Pin toolbar',
		description:
			'The text is shown as the accessible label for a button in the OmniBar toolbar. When clicked, the button pins (keeps visible) the toolbar so it remains on screen permanently.',
	},
	comment: {
		id: 'experiment-omnibar.comments.comment-button',
		defaultMessage: 'Comment',
		description: 'Label for the comment button in the OmniBar',
	},
});

const styles = cssMap({
	responsiveContainer: {
		position: 'sticky',
		width: '100%',
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		height: '72px',
		bottom: token('space.0'),
		paddingBottom: token('space.300'),
		zIndex: 1,
		overflow: 'visible',
	},
	responsiveContainerUnpinned: {
		maxWidth: '800px',
		marginInline: 'auto',
	},
	responsiveContainerInner: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-end',
	},
	barContainerBase: {
		width: 'fit-content',
		pointerEvents: 'auto',
		opacity: 1,
		transform: 'translateY(0)',
	},
	barHidden: {
		transform: 'translateY(100%)',
		opacity: 0,
		pointerEvents: 'none',
	},
	barSlideIn: {
		transform: 'translateY(0)',
		opacity: 1,
		pointerEvents: 'auto',
	},
	barNoTransition: {
		transition: 'none',
	},
	barTransition: {
		transition: 'transform 200ms ease-out, opacity 200ms ease-out',
	},
	buttonStyle: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.raised'),
	},
	reactionsContainerBase: {
		padding: token('space.100'),
		overflow: 'visible',
	},
	commentsContainerBase: {
		padding: token('space.100'),
	},
	noPointerEvents: {
		pointerEvents: 'none',
	},
	containerHiddenWhenDimensionsNotReady: {
		visibility: 'hidden',
	},
	pinContainerBase: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.full'),
		transition: 'opacity 200ms ease-out',
	},
	horizontalCenteringFixForToggleButton: {
		paddingLeft: token('space.500'),
	},
	pinButtonHiddenWhenNotHovered: {
		opacity: 0,
		pointerEvents: 'none',
	},
});

export const OmniBar: React.FC<OmniBarProps> = ({
	contentId,
	spaceId,
	contentType,
	contentSubType,
	onCommentClick,
}) => {
	const isSSR = typeof window === 'undefined';
	const initialPinState = getPinStateFromStorage();

	/**
	 * Detect if an SSR'd omnibar is already visible — call only once and cache in ref.
	 * This must not be called on every render because subsequent renders commit client
	 * styles to the DOM, making the check return stale results.
	 */
	const wasOmnibarSSRdRef = React.useRef<boolean | null>(null);
	if (wasOmnibarSSRdRef.current === null) {
		wasOmnibarSSRdRef.current = isSSR || wasOmnibarSSRd();
	}
	const isHydratingAfterSSR = wasOmnibarSSRdRef.current;
	const [hasHydrated, setHasHydrated] = useState(!isHydratingAfterSSR);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isPinned, setIsPinned] = useState(initialPinState);
	const [isHovered, setIsHovered] = useState(false);
	const [shouldHideInstantly, setHideInstantly] = useState(false);
	const [isAnimatingOut, setIsAnimatingOut] = useState(false);
	const [hasActiveInteraction, setHasActiveInteraction] = useState(false);
	const [hasCompletedInitialSlideUp, setHasCompletedInitialSlideUp] = useState(isHydratingAfterSSR);
	const [isAnimatingOutForUnmount, setIsAnimatingOutForUnmount] = useState(false);
	const hasHoveredRef = React.useRef(hasEverMounted);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const isMountedRef = React.useRef(true);
	const barWasVisibleRef = React.useRef(false);
	// Track if we rendered on server to know whether to skip animation
	const wasServerRenderedRef = React.useRef(isHydratingAfterSSR);
	useEffect(() => {
		if (isHydratingAfterSSR) {
			setHasHydrated(true);
		}
	}, [isHydratingAfterSSR]);

	const handleOmniBarMouseEnter = useCallback(() => {
		if (isAnimatingOut) {
			return;
		}
		hasHoveredRef.current = true;
		setHideInstantly(false);
		setIsHovered(true);
	}, [isAnimatingOut]);
	const handleOmniBarMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container || isPinned) return;

		const handleNativeMouseLeave = (e: MouseEvent) => {
			const relatedTarget = e.relatedTarget as HTMLElement | null;
			if (relatedTarget) {
				if (container.contains(relatedTarget)) return;
			}
			// Don't hide if a popup interaction is active (emoji picker, summary popup)
			if (hasActiveInteraction) return;
			setIsAnimatingOut(true);
			setIsHovered(false);
			setTimeout(() => {
				setIsAnimatingOut(false);
			}, 300);
		};

		container.addEventListener('mouseleave', handleNativeMouseLeave);
		return () => {
			container.removeEventListener('mouseleave', handleNativeMouseLeave);
		};
	}, [isPinned, hasActiveInteraction]);
	const shouldHideExternally = useShouldHideOmniBar();
	const hasReceivedHideRequest = useHasReceivedOmniBarHideRequest();

	const { mode: rovoMode } = useRovoExperienceAPIStore();
	const isAIMiniModalOpen = rovoMode === 'mini-modal';
	const { handleCommentClick } = useOmnibarCommentTrigger(onCommentClick);

	const { showReactions, showComments } = useOmnibarEligibilityFromContext();

	const showCommentButton = showComments && showReactions;

	const { reactionsData, isLoading: reactionsLoading } = useOmnibarReactionsData({
		contentId,
		spaceId,
		contentType,
		skip: !showReactions,
	});

	const pageContentDimensions = usePageContentDimensions();

	const minContentAreaWidth = 620;
	const shouldHideForSmallViewport =
		!isSSR &&
		typeof pageContentDimensions.width === 'number' &&
		pageContentDimensions.width > 0 &&
		pageContentDimensions.width <= minContentAreaWidth;

	const isBarVisible = !shouldHideForSmallViewport && showReactions;
	const hasFiredRenderedRef = React.useRef(false);

	useEffect(() => {
		if (!isBarVisible || hasFiredRenderedRef.current) return;
		hasFiredRenderedRef.current = true;
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'omniBar',
				actionSubjectId: 'omniBarRendered',
				source: OMNIBAR_ANALYTICS_SOURCE,
			},
		}).fire();
	}, [createAnalyticsEvent, isBarVisible]);

	const isContainerVisible =
		typeof pageContentDimensions.width !== 'number' || pageContentDimensions.width > 0;

	useEffect(() => {
		if (!isContainerVisible) return;

		// If this was rendered on the server, skip animation on SPA hydration
		if (wasServerRenderedRef.current) {
			hasEverMounted = true;
			return;
		}

		isMountedRef.current = true;
		const frameId = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (isMountedRef.current) {
					setHasCompletedInitialSlideUp(true);
					hasEverMounted = true;
				}
			});
		});
		return () => {
			isMountedRef.current = false;
			cancelAnimationFrame(frameId);
		};
	}, [isContainerVisible]);

	const isContainerHiddenForLayoutShift =
		!isSSR &&
		hasHydrated &&
		typeof pageContentDimensions.width === 'number' &&
		pageContentDimensions.width === 0;

	const handlePinToggle = useCallback(() => {
		if (!isPinned) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'pinned',
					actionSubject: 'button',
					actionSubjectId: 'omniBarPinned',
					source: OMNIBAR_ANALYTICS_SOURCE,
				},
			}).fire();
			setHideInstantly(true);
			setIsAnimatingOut(false);
			setIsPinned(true);
			persistPinState(true);
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- intentional rAF to clear hideInstantly after paint
			requestAnimationFrame(() => setHideInstantly(false));
		} else {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'unpinned',
					actionSubject: 'button',
					actionSubjectId: 'omniBarUnpinned',
					source: OMNIBAR_ANALYTICS_SOURCE,
				},
			}).fire();
			setIsAnimatingOut(true);
			setIsHovered(false);
			setHasActiveInteraction(false);
			setIsPinned(false);
			persistPinState(false);
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- intentional delayed reset after unpin animation
			setTimeout(() => setIsAnimatingOut(false), 200);
		}
	}, [isPinned, createAnalyticsEvent]);

	const renderIcon = useCallback(
		(iconProps: React.ComponentProps<typeof ChevronDownIcon>) =>
			isPinned ? <ChevronDownIcon {...iconProps} /> : <ChevronUpIconRender {...iconProps} />,
		[isPinned],
	);

	const barIsVisible =
		!shouldHideExternally &&
		!isAIMiniModalOpen &&
		((isPinned && hasCompletedInitialSlideUp) || isHovered || hasActiveInteraction);

	// When bar becomes hidden (unpin or external request), keep content mounted for slide-down animation
	/* eslint-disable @atlassian/perf-linting/no-chain-state-updates -- intentional: deferred setState after 250ms for slide-down animation */
	useEffect(() => {
		if (barIsVisible) {
			barWasVisibleRef.current = true;
			setIsAnimatingOutForUnmount(false);
			return;
		}
		if (barWasVisibleRef.current) {
			barWasVisibleRef.current = false;
			setIsAnimatingOutForUnmount(true);
			const timer = setTimeout(() => setIsAnimatingOutForUnmount(false), 250);
			return () => clearTimeout(timer);
		}
	}, [barIsVisible]);
	/* eslint-enable @atlassian/perf-linting/no-chain-state-updates */

	const shouldRenderBarContent = barIsVisible || isAnimatingOutForUnmount;

	if (shouldHideForSmallViewport || !showReactions) {
		return null;
	}

	return (
		<>
			<Box
				ref={containerRef}
				testId="omni-bar"
				xcss={cx(
					styles.responsiveContainer,
					!isPinned && fg('cc-engagement-omnibar-width-fix') && styles.responsiveContainerUnpinned,
					shouldHideExternally && styles.noPointerEvents,
					isContainerHiddenForLayoutShift && styles.containerHiddenWhenDimensionsNotReady,
				)}
				onMouseEnter={handleOmniBarMouseEnter}
				onFocus={handleOmniBarMouseEnter}
				onMouseLeave={handleOmniBarMouseLeave}
				onBlur={handleOmniBarMouseLeave}
			>
				<Box xcss={styles.responsiveContainerInner}>
					<Inline
						space="space.100"
						alignBlock="center"
						xcss={cx(
							styles.barContainerBase,
							styles.horizontalCenteringFixForToggleButton,
							styles.barNoTransition,
							(!isPinned ||
								shouldHideExternally ||
								isAIMiniModalOpen ||
								(isPinned && !hasCompletedInitialSlideUp)) &&
								styles.barHidden,
							!shouldHideInstantly &&
								!shouldHideExternally &&
								!isAIMiniModalOpen &&
								((isPinned && hasCompletedInitialSlideUp) || isHovered || hasActiveInteraction) &&
								styles.barSlideIn,
							(hasHoveredRef.current ||
								(hasReceivedHideRequest && fg('cc-engagement-omnibar-transition-fix'))) &&
								styles.barTransition,
						)}
					>
						{shouldRenderBarContent && (
							<>
								{showReactions && (
									<Box xcss={cx(styles.reactionsContainerBase, styles.buttonStyle)}>
										<OmniBarReactions
											contentId={contentId}
											spaceId={spaceId}
											contentType={contentType}
											contentSubType={contentSubType}
											reactionsData={reactionsData}
											isLoading={reactionsLoading}
											shouldCloseInteractions={
												isAnimatingOutForUnmount ||
												shouldHideExternally ||
												isAIMiniModalOpen ||
												(!isPinned && !isHovered && !hasActiveInteraction)
											}
											shouldShowBloom={
												!isAnimatingOutForUnmount &&
												!shouldHideExternally &&
												!isAIMiniModalOpen &&
												((isPinned && hasCompletedInitialSlideUp) ||
													isHovered ||
													hasActiveInteraction)
											}
											onInteractionStateChange={setHasActiveInteraction}
										/>
									</Box>
								)}
								{showCommentButton && (
									<Box xcss={cx(styles.commentsContainerBase, styles.buttonStyle)}>
										<Tooltip
											position="top"
											content={formatMessage(i18n.comment)}
											hideTooltipOnClick
										>
											<Button
												appearance="subtle"
												onClick={handleCommentClick}
												iconBefore={CommentAddIcon}
											>
												{formatMessage(i18n.comment)}
											</Button>
										</Tooltip>
									</Box>
								)}
								<Box
									xcss={cx(
										styles.pinContainerBase,
										isPinned && !isHovered && styles.pinButtonHiddenWhenNotHovered,
									)}
								>
									<Tooltip
										position="top"
										content={
											isPinned ? formatMessage(i18n.hideOmniBar) : formatMessage(i18n.pinOmniBar)
										}
										hideTooltipOnClick
									>
										<IconButton
											appearance="subtle"
											shape="circle"
											icon={renderIcon}
											label={
												isPinned ? formatMessage(i18n.hideOmniBar) : formatMessage(i18n.pinOmniBar)
											}
											onClick={handlePinToggle}
										/>
									</Tooltip>
								</Box>
							</>
						)}
					</Inline>
				</Box>
			</Box>
			<ExperienceSuccess name={VIEW_OMNIBAR_EXPERIENCE} />
		</>
	);
};
